import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { XCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Transition } from '@headlessui/react';

export default function BusinessDetailsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [businessData, setBusinessData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    gstNumber: '',
    website: '',
    quantityPerBox: ''
  });
  const [validationErrors, setValidationErrors] = useState({
    phone: '',
    email: '',
    gstNumber: ''
  });

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  useEffect(() => {
    const fetchBusinessDetails = async () => {
      try {
        const docRef = doc(db, 'settings', 'business');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setBusinessData(docSnap.data());
        }
      } catch (error) {
        console.error('Error fetching business details:', error);
        setError('Failed to load business details');
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessDetails();
  }, []);

  const validatePhone = (phone) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phone) return '';
    if (!phoneRegex.test(phone)) {
      return 'Please enter a valid phone number';
    }
    return '';
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return '';
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validateGST = (gst) => {
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!gst) return '';
    if (!gstRegex.test(gst)) {
      return 'Please enter a valid GST number (e.g., 29ABCDE1234F1Z5)';
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setBusinessData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'phone') {
      setValidationErrors(prev => ({
        ...prev,
        phone: validatePhone(value)
      }));
    } else if (name === 'email') {
      setValidationErrors(prev => ({
        ...prev,
        email: validateEmail(value)
      }));
    } else if (name === 'gstNumber') {
      setValidationErrors(prev => ({
        ...prev,
        gstNumber: validateGST(value)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const phoneError = validatePhone(businessData.phone);
    const emailError = validateEmail(businessData.email);
    const gstError = validateGST(businessData.gstNumber);

    setValidationErrors({
      phone: phoneError,
      email: emailError,
      gstNumber: gstError
    });

    if (phoneError || emailError || gstError) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      await setDoc(doc(db, 'settings', 'business'), businessData);
      setSuccess('Business details updated successfully!');
    } catch (error) {
      console.error('Error updating business details:', error);
      setError('Failed to update business details');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <>
      <div aria-live="assertive" className="fixed inset-0 flex items-end px-4 py-6 pointer-events-none sm:p-6 z-50">
        <div className="w-full flex flex-col items-center space-y-4 sm:items-end">
          <Transition
            show={!!success}
            enter="transform ease-out duration-300 transition"
            enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            enterTo="translate-y-0 opacity-100 sm:translate-x-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="max-w-sm w-full bg-white dark:bg-[#2E3B4E] shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5">
              <div className="p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-6 w-6 text-green-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3 w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{success}</p>
                  </div>
                  <div className="ml-4 flex-shrink-0 flex">
                    <button
                      type="button"
                      className="bg-white dark:bg-[#2E3B4E] rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none"
                      onClick={() => setSuccess('')}
                    >
                      <span className="sr-only">Close</span>
                      <XCircleIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Transition>

          <Transition
            show={!!error}
            enter="transform ease-out duration-300 transition"
            enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            enterTo="translate-y-0 opacity-100 sm:translate-x-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="max-w-sm w-full bg-white dark:bg-[#2E3B4E] shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5">
              <div className="p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <XCircleIcon className="h-6 w-6 text-red-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3 w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{error}</p>
                  </div>
                  <div className="ml-4 flex-shrink-0 flex">
                    <button
                      type="button"
                      className="bg-white dark:bg-[#2E3B4E] rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none"
                      onClick={() => setError('')}
                    >
                      <span className="sr-only">Close</span>
                      <XCircleIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>

      <div className="bg-white dark:bg-[#2E3B4E] rounded-lg shadow">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label 
                htmlFor="name" 
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Business Name
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={businessData.name}
                onChange={handleChange}
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 
                  bg-white dark:bg-gray-700 px-4 py-3 text-gray-900 dark:text-white 
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                  shadow-sm text-base"
                required
              />
            </div>

            <div>
              <label 
                htmlFor="phone" 
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                id="phone"
                value={businessData.phone}
                onChange={handleChange}
                className={`block w-full rounded-md border ${
                  validationErrors.phone ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                } bg-white dark:bg-gray-700 px-4 py-3 text-gray-900 dark:text-white 
                focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                shadow-sm text-base`}
              />
              {validationErrors.phone && (
                <p className="mt-1 text-sm text-red-500">{validationErrors.phone}</p>
              )}
            </div>

            <div>
              <label 
                htmlFor="email" 
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Email Address
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={businessData.email}
                onChange={handleChange}
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 
                  bg-white dark:bg-gray-700 px-4 py-3 text-gray-900 dark:text-white 
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                  shadow-sm text-base"
              />
              {validationErrors.email && (
                <p className="text-sm text-red-500">{validationErrors.email}</p>
              )}
            </div>

            <div>
              <label 
                htmlFor="website" 
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Website
              </label>
              <input
                type="url"
                name="website"
                id="website"
                value={businessData.website}
                onChange={handleChange}
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 
                  bg-white dark:bg-gray-700 px-4 py-3 text-gray-900 dark:text-white 
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                  shadow-sm text-base"
              />
            </div>

            <div>
              <label 
                htmlFor="gstNumber" 
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                GST Number
              </label>
              <input
                type="text"
                name="gstNumber"
                id="gstNumber"
                value={businessData.gstNumber}
                onChange={handleChange}
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 
                  bg-white dark:bg-gray-700 px-4 py-3 text-gray-900 dark:text-white 
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                  shadow-sm text-base"
              />
              {validationErrors.gstNumber && (
                <p className="text-sm text-red-500">{validationErrors.gstNumber}</p>
              )}
            </div>

            <div>
              <label 
                htmlFor="quantityPerBox" 
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Quantity per Box
              </label>
              <input
                type="number"
                name="quantityPerBox"
                id="quantityPerBox"
                value={businessData.quantityPerBox}
                onChange={handleChange}
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 
                  bg-white dark:bg-gray-700 px-4 py-3 text-gray-900 dark:text-white 
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                  shadow-sm text-base"
                min="1"
              />
            </div>

            <div className="sm:col-span-2">
              <label 
                htmlFor="address" 
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Address
              </label>
              <textarea
                name="address"
                id="address"
                rows={3}
                value={businessData.address}
                onChange={handleChange}
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 
                  bg-white dark:bg-gray-700 px-4 py-3 text-gray-900 dark:text-white 
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                  shadow-sm text-base"
              />
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex justify-center rounded-md border border-transparent 
                bg-primary-600 px-6 py-3 text-base font-medium text-white shadow-sm 
                hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 
                focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}